// Theme utility functions for applying theme classes

export const getThemeClasses = (theme) => {
  const baseClasses = {
    light: {
      bg: 'bg-gradient-to-br from-slate-50 to-blue-50',
      bgDark: 'bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900',
      bgCard: 'bg-white',
      text: 'text-slate-900',
      textSecondary: 'text-slate-600',
      textLight: 'text-white',
      border: 'border-slate-200',
      primary: 'bg-indigo-500',
      accent: 'bg-purple-500',
      success: 'bg-emerald-500',
      warning: 'bg-orange-400',
      error: 'bg-red-500',
    },
    dark: {
      bg: 'bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900',
      bgDark: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      bgCard: 'bg-slate-800 bg-opacity-80 backdrop-blur-md',
      text: 'text-slate-100',
      textSecondary: 'text-slate-300',
      textLight: 'text-white',
      border: 'border-slate-600 border-opacity-50',
      primary: 'bg-indigo-400',
      accent: 'bg-purple-400',
      success: 'bg-emerald-400',
      warning: 'bg-yellow-400',
      error: 'bg-red-400',
    },
  };

  return baseClasses[theme] || baseClasses.light;
};

export const getThemeTextClasses = (theme) => {
  const classes = {
    light: {
      primary: 'text-slate-900',
      secondary: 'text-slate-600',
      light: 'text-white',
      accent: 'text-indigo-600',
    },
    dark: {
      primary: 'text-slate-100',
      secondary: 'text-slate-300',
      light: 'text-white',
      accent: 'text-indigo-300',
    },
  };

  return classes[theme] || classes.light;
};

// Custom hook for theme styles
export const useThemeStyles = (theme = 'light') => {
  const bgClass = theme === 'dark'
    ? 'bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900'
    : 'bg-gradient-to-br from-slate-50 to-blue-50';
  
  const cardBg = theme === 'dark'
    ? 'bg-slate-800 bg-opacity-80 backdrop-blur-md'
    : 'bg-white';
  
  const textColor = theme === 'dark' ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = theme === 'dark' ? 'text-slate-300' : 'text-slate-600';
  const borderColor = theme === 'dark' ? 'border-slate-600 border-opacity-50 border-2' : 'border-slate-200 border-2';

  return {
    bgClass,
    cardBg,
    textColor,
    textSecondary,
    borderColor
  };
};

/**
 * Get consistent medication colors by index or medication ID
 * @param {number|string} identifier - Either an index number or medication ID
 * @param {Array} medications - Array of medications (only needed if identifier is an ID)
 * @returns {Object} Color object with light, dark, border, and text properties
 */
export const getMedicationColor = (identifier, medications = []) => {
  let index;

  // If identifier is a string, assume it's a medication ID and find the index
  if (typeof identifier === 'string') {
    index = medications.findIndex(m => m.id === identifier);
    if (index === -1) return null;
  } else {
    // If identifier is a number, use it directly as index
    index = identifier;
  }

  const colors = [
    {
      light: 'bg-gradient-to-br from-blue-50 to-blue-100',
      dark: 'bg-gradient-to-br from-blue-900/30 to-blue-800/30',
      border: 'border-blue-300',
      text: 'text-blue-900'
    },
    {
      light: 'bg-gradient-to-br from-purple-50 to-purple-100',
      dark: 'bg-gradient-to-br from-purple-900/30 to-purple-800/30',
      border: 'border-purple-300',
      text: 'text-purple-900'
    },
    {
      light: 'bg-gradient-to-br from-pink-50 to-pink-100',
      dark: 'bg-gradient-to-br from-pink-900/30 to-pink-800/30',
      border: 'border-pink-300',
      text: 'text-pink-900'
    },
    {
      light: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
      dark: 'bg-gradient-to-br from-indigo-900/30 to-indigo-800/30',
      border: 'border-indigo-300',
      text: 'text-indigo-900'
    },
    {
      light: 'bg-gradient-to-br from-cyan-50 to-cyan-100',
      dark: 'bg-gradient-to-br from-cyan-900/30 to-cyan-800/30',
      border: 'border-cyan-300',
      text: 'text-cyan-900'
    },
    {
      light: 'bg-gradient-to-br from-teal-50 to-teal-100',
      dark: 'bg-gradient-to-br from-teal-900/30 to-teal-800/30',
      border: 'border-teal-300',
      text: 'text-teal-900'
    },
    {
      light: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
      dark: 'bg-gradient-to-br from-emerald-900/30 to-emerald-800/30',
      border: 'border-emerald-300',
      text: 'text-emerald-900'
    },
    {
      light: 'bg-gradient-to-br from-lime-50 to-lime-100',
      dark: 'bg-gradient-to-br from-lime-900/30 to-lime-800/30',
      border: 'border-lime-300',
      text: 'text-lime-900'
    },
    {
      light: 'bg-gradient-to-br from-amber-50 to-amber-100',
      dark: 'bg-gradient-to-br from-amber-900/30 to-amber-800/30',
      border: 'border-amber-300',
      text: 'text-amber-900'
    },
    {
      light: 'bg-gradient-to-br from-orange-50 to-orange-100',
      dark: 'bg-gradient-to-br from-orange-900/30 to-orange-800/30',
      border: 'border-orange-300',
      text: 'text-orange-900'
    },
    {
      light: 'bg-gradient-to-br from-red-50 to-red-100',
      dark: 'bg-gradient-to-br from-red-900/30 to-red-800/30',
      border: 'border-red-300',
      text: 'text-red-900'
    },
    {
      light: 'bg-gradient-to-br from-rose-50 to-rose-100',
      dark: 'bg-gradient-to-br from-rose-900/30 to-rose-800/30',
      border: 'border-rose-300',
      text: 'text-rose-900'
    },
    {
      light: 'bg-gradient-to-br from-violet-50 to-violet-100',
      dark: 'bg-gradient-to-br from-violet-900/30 to-violet-800/30',
      border: 'border-violet-300',
      text: 'text-violet-900'
    },
    {
      light: 'bg-gradient-to-br from-fuchsia-50 to-fuchsia-100',
      dark: 'bg-gradient-to-br from-fuchsia-900/30 to-fuchsia-800/30',
      border: 'border-fuchsia-300',
      text: 'text-fuchsia-900'
    },
    {
      light: 'bg-gradient-to-br from-sky-50 to-sky-100',
      dark: 'bg-gradient-to-br from-sky-900/30 to-sky-800/30',
      border: 'border-sky-300',
      text: 'text-sky-900'
    },
  ];

  return colors[index % colors.length];
};

/**
 * Common button style constants for consistent UI
 */
export const buttonStyles = {
  // Common interactive button classes
  interactive: 'hover:scale-105 transition-all flex flex-col items-center justify-center',

  // Action button variants
  primary: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 text-white font-bold shadow-lg',
  secondary: 'bg-gradient-to-r from-orange-400 to-pink-400 hover:brightness-110 text-white font-bold shadow-lg',
  danger: 'bg-gradient-to-r from-slate-600 to-slate-700 hover:brightness-110 text-white font-bold shadow-lg',
  accent: 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:brightness-110 text-white font-bold shadow-lg',

  // Size variants
  small: 'py-3 sm:py-4 md:py-5 lg:py-6 rounded-xl sm:rounded-2xl text-xs sm:text-sm md:text-base lg:text-lg',
  large: 'py-12 px-12 rounded-3xl text-4xl shadow-2xl',
  medium: 'py-8 px-8 rounded-2xl text-3xl shadow-xl',
};

/**
 * Standardized error handling utility
 * @param {Error} error - The error object
 * @param {string} operation - Description of the operation that failed
 * @param {boolean} shouldThrow - Whether to re-throw the error (default: true)
 */
export const handleError = (error, operation, shouldThrow = true) => {
  console.error(`Error ${operation}:`, error);
  if (shouldThrow) {
    throw error;
  }
};

/**
 * Common prop patterns for medication-related components
 */
export const componentProps = {
  // Common medication component props
  medication: {
    userName: '',
    medications: [],
    medicationLogs: [],
    medicationsLoading: false,
    currentMedIndex: 0,
    theme: 'light',
    onNavigate: () => {},
  },

  // Voice interaction props
  voice: {
    isListening: false,
    voiceStatus: 'ready',
    transcript: '',
    startListening: () => {},
    stopListening: () => {},
  },

  // Medication action props
  actions: {
    onMedicationTaken: null,
    onMedicationSkipped: () => {},
  },
};

/**
 * Utility to merge component props with defaults
 * @param {Object} componentSpecificProps - Props specific to the component
 * @param {Array} propCategories - Array of prop categories to include
 * @returns {Object} Merged props object
 */
export const withCommonProps = (componentSpecificProps, propCategories = []) => {
  const mergedProps = { ...componentSpecificProps };

  propCategories.forEach(category => {
    if (componentProps[category]) {
      Object.assign(mergedProps, componentProps[category]);
    }
  });

  return mergedProps;
};

